import { ApplicationContext } from '@hc-frontend/core-context';
import type {
  FullURL,
  PageIdentifier,
  ProductType,
  SessionId,
  SubProductType,
  UserId,
} from '@hc-frontend/core-types';
import { CallType, PhoneCampaign } from '@hc-frontend/core-types';
import type { IOperationResult } from '@hc-frontend/core-utils-business-operation';
import {
  createErrorOperationResult,
  createSuccessOperationResult,
} from '@hc-frontend/core-utils-business-operation';
import { toPhoneE164 } from '@hc-frontend/core-utils-format';
import { fetchBasedCall } from '@hc-frontend/core-utils-net';

import type { Schedule } from './fetch-phone-campaign-btyp.types';
import {
  buildMedAdvDayListSchedule,
  formatHours,
  getCallCampaignProduct,
} from './utils';

interface GetCallCampaignResponse {
  placement: string;
  logo: string | null;
  advertiser: {
    name: string;
    logo?: string | null;
  } | null;
  phone: {
    promo_number_formatted: string;
    promo_number: string;
    tracking_url?: string | null;
    link_invoca: string;
    tty_formatted: string;
  };
  state: string;
  zip: string;
  impression_id: string;
  schedule: Schedule;
  error?: string;
  status?: number;
}

interface GetCallCampaignPayload {
  call_type: CallType;
  product: string;
  placement: string;
  site: string;
  zip: string;
  state: string;
  src: string;
  user_id: string;
  sub_id: string | number;
  session_id: string;
  request_url: string;
  profile: object;
  smid: string;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const FEATURES_CALL_CAMPAIGN_ERRORS = [
  {
    error: 'failOnGetCampaignsRepository',
    description: 'Error during the getCampaignsRepository implementation',
  },
  {
    error: 'campaignIsInactiveOrEmpty',
    description: 'Campaign is inactive or empty',
  },
] as const;

type FeaturesCallCampaignError =
  (typeof FEATURES_CALL_CAMPAIGN_ERRORS)[number]['error'];

export async function fetchPhoneCampaignBtyp({
  baseURL,
  productType,
  subProduct,
  placement,
  location,
  tracking,
  identifier,
  snowplowData = '',
  requestUrl,
  standardizedMaterialId,
  log,
}: {
  baseURL: string;
  productType: ProductType;
  subProduct?: SubProductType;
  placement: string;
  location: {
    zipCode: string;
    stateAcronym: string;
  } | null;
  tracking: {
    source?: string;
    subid?: string;
  } | null;
  identifier: {
    userId: UserId;
    sessionId: SessionId;
  };
  standardizedMaterialId?: PageIdentifier;
  snowplowData?: string;
  requestUrl: FullURL;
  log: ApplicationContext['log'];
}): Promise<IOperationResult<PhoneCampaign, FeaturesCallCampaignError>> {
  if (!snowplowData) {
    log({
      type: 'info',
      context: 'server',
      message: 'snowplowData is not provided',
      data: {
        sessionId: identifier.sessionId,
        userId: identifier.userId,
        productType,
        rootCause: new Error('Snowplow data is not provided.'),
      },
    });
  }

  const callCampaignProduct = getCallCampaignProduct(productType, subProduct);
  const callType = CallType.enum.bundle;

  const body = {
    call_type: callType,
    product: callCampaignProduct,
    placement,
    site: 'www.healthcare.com',
    zip: location ? location.zipCode : '',
    state: location ? location.stateAcronym : '',
    src: tracking && tracking.source ? tracking.source : 'ORGANIC',
    sub_id: tracking && tracking.subid ? tracking.subid : '',
    user_id: identifier.userId,
    session_id: identifier.sessionId,
    request_url: requestUrl,
    smid: standardizedMaterialId ?? '',
    profile: {},
  };

  try {
    const response = await fetchBasedCall<
      GetCallCampaignPayload,
      GetCallCampaignResponse
    >({
      url: `${baseURL}/api/v1/call/campaign/btyp`,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'snowplow-data': snowplowData,
      },
      body,
      next: {
        revalidate: 0,
      },
    });

    if (response.result !== 'success' || !response.body) {
      return createErrorOperationResult({
        errors: [
          {
            code: 'campaignIsInactiveOrEmpty',
            rootCause: new Error('Campaign is inactive or empty'),
          },
        ],
      });
    }

    const weekDay = new Date().getDay();
    const day = [
      'sunday',
      'monday',
      'tuesday',
      'wednesday',
      'thursday',
      'friday',
      'saturday',
    ];
    const schedule = response.body.schedule;
    const scheduleKey = day[weekDay] as keyof typeof schedule;
    const scheduleDay = response.body.schedule[scheduleKey];

    const phoneNumber = PhoneCampaign.safeParse({
      phoneNumber: response.body.phone.promo_number_formatted,
      todayTTY: `TTY Users: 711 | Today: ${formatHours(scheduleDay.from)} - ${formatHours(scheduleDay.to)} (EST)`,
      weekTTY: response.body.phone.tty_formatted,
      tty: 'TTY 711',
      schedule:
        callCampaignProduct === 'MEDICARE_ADVANTAGE'
          ? buildMedAdvDayListSchedule(day, schedule)
          : response.body.phone.tty_formatted.split('|')[1],
      href: `tel:${toPhoneE164(response.body.phone.promo_number_formatted)}`,
      product: productType,
      placement: response.body.placement,
      advertiser: response.body.advertiser?.name ?? 'Advertiser', // TODO: Remove this once the advertiser is added to the response
      callType: callType,
    });

    if (!phoneNumber.success) {
      const errorResult = createErrorOperationResult({
        errors: [
          {
            code: 'failOnGetCampaignsRepository',
            rootCause: phoneNumber.error,
          },
        ],
      });

      return errorResult;
    }

    return createSuccessOperationResult(phoneNumber.data);
  } catch (error) {
    return createErrorOperationResult({
      errors: [
        {
          code: 'campaignIsInactiveOrEmpty',
          rootCause: error as Error,
        },
      ],
    });
  }
}
